import React from 'react';

import { Icon } from '../icon';

import * as Styles from './styles';
import { ImageProps } from './types';

// FIX-OPS-UPLOAD-TYPES (owner, 2026-10-15): image types every browser the
// office uses can draw. HEIC/HEIF (iPhone originals) only draw in Safari and
// PDFs never draw in an <img>: those open as a file, never a broken image.
const BROWSER_IMAGE = /\.(png|jpe?g|gif|webp|avif)$/i;
const BROWSER_IMAGE_DATA = /^data:image\/(png|jpe?g|gif|webp|avif)[;,]/i;

export const isBrowserImage = (src?: string) => {
  if (!src) {
    return false;
  }
  if (src.startsWith('data:')) {
    return BROWSER_IMAGE_DATA.test(src);
  }
  try {
    return BROWSER_IMAGE.test(new URL(src, 'https://x.invalid').pathname);
  } catch {
    return false;
  }
};

/**
 * Opens a stored receipt: a drawable image in the gallery (`openGallery`),
 * anything else (PDF, HEIC) in a new tab, where the browser shows or
 * downloads it.
 */
export const openReceipt = (
  url: string | undefined,
  openGallery: () => void
) => {
  if (!url) {
    return;
  }
  if (isBrowserImage(url)) {
    openGallery();
    return;
  }
  // Only a web address is opened: a stored value such as javascript: or
  // data: never runs.
  let protocol = '';
  try {
    protocol = new URL(url, window.location.href).protocol;
  } catch {
    return;
  }
  if (protocol === 'https:' || protocol === 'http:') {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

const Image: React.FC<ImageProps> = ({
  src,
  alt,
  style,
  width,
  height,
  onLoad,
  onError,
  onClick,
  clickable,
  objectFit = 'fill',
  ...props
}) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  const [source, setSource] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    setSource(src);
    setFailed(false);
    setIsLoading(src !== undefined);
  }, [src]);

  const loaderSize = Math.min(40, height || 40);

  return (
    <Styles.ImageBlock
      onClick={onClick}
      clickable={clickable || !!onClick}
      style={{
        width,
        height,
        ...style,
      }}
    >
      {failed ? (
        // A file the browser cannot draw (PDF, HEIC outside Safari): a file
        // tile in the same box, never the broken-image glyph.
        <Styles.Fallback
          role="img"
          aria-label={alt}
          data-testid="image-fallback"
        >
          <Icon.FileFilled />
        </Styles.Fallback>
      ) : (
        <Styles.ImageView
          alt={alt}
          src={source}
          isLoaded={isLoading}
          style={{
            objectFit,
          }}
          onLoad={(e) => {
            onLoad?.(e);
            setIsLoading(false);
          }}
          onError={(e) => {
            setIsLoading(false);
            setFailed(true);
            onError?.(e);
          }}
          {...props}
        />
      )}

      {isLoading && <Styles.Loader width={loaderSize} height={loaderSize} />}
    </Styles.ImageBlock>
  );
};

export { Image };
